
import React from "react";
import { useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import StructuredData from "@/components/StructuredData";
import GeoLocalizedContent from "@/components/seo/GeoLocalizedContent";
import { useGeoLocalizedData } from "@/hooks/useGeoLocalizedData";
import { departmentSEOContent } from "@/data/geographic/departmentContent";

const CityPage: React.FC = () => {
  const { departmentCode, city } = useParams<{ departmentCode: string; city: string }>();
  
  // Trouver le département qui contient cette ville
  const findDepartmentForCity = (cityName: string) => {
    return Object.values(departmentSEOContent).find(dept => 
      dept.mainCities.some(c => c.toLowerCase().replace(/[- ]/g, '') === cityName?.toLowerCase().replace(/[- ]/g, ''))
    );
  };

  const targetDept = departmentCode ? departmentSEOContent[departmentCode] : findDepartmentForCity(city || '');
  const { departmentData, localBusinessSchema, seoMetadata } = useGeoLocalizedData(
    targetDept?.code, 
    city
  );

  // Vérifier que la ville existe dans le département
  const cityExists = targetDept && city && targetDept.mainCities.some(c => 
    c.toLowerCase().replace(/[- ]/g, '') === city.toLowerCase().replace(/[- ]/g, '')
  );

  if (!cityExists || !departmentData || !seoMetadata) {
    return <Navigate to="/nos-services" replace />;
  }

  const formattedCity = targetDept.mainCities.find(c => 
    c.toLowerCase().replace(/[- ]/g, '') === city?.toLowerCase().replace(/[- ]/g, '')
  ) || city;

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={`Gestion des déchets ${formattedCity} (${targetDept.code}) - Groupe Fabre Environnement`}
        description={`Services de gestion des déchets à ${formattedCity} dans le ${targetDept.name}. Location bennes, recyclage métaux, valorisation. Intervention rapide.`}
        canonical={`/services/${targetDept.code}/${city}`}
        keywords={`gestion déchets ${formattedCity}, benne ${formattedCity}, recyclage ${formattedCity}, déchets ${targetDept.name}`}
        localBusiness={{
          city: formattedCity,
          region: targetDept.name,
          postalCode: targetDept.code + "000",
          serviceType: "Gestion des déchets"
        }}
      />
      
      {localBusinessSchema && <StructuredData data={localBusinessSchema} />}
      
      <Navbar />
      
      <main className="flex-1 pt-20">
        <nav aria-label="Fil d'Ariane" className="bg-gray-50 py-3 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <Breadcrumb
              items={[
                { label: "Accueil", href: "/" },
                { label: "Services", href: "/nos-services" },
                { label: `${targetDept.name} (${targetDept.code})`, href: `/services/departement/${targetDept.code}` },
                { label: formattedCity, href: `/services/${targetDept.code}/${city}`, current: true }
              ]}
            />
          </div>
        </nav>

        <article className="py-12">
          <div className="container mx-auto px-4">
            {/* Section spécifique à la ville */}
            <section className="mb-12">
              <div className="bg-white rounded-lg shadow-md p-8">
                <h1 className="text-3xl font-bold text-fabre-gray mb-6">
                  Gestion des déchets à {formattedCity}
                </h1>
                <div className="prose max-w-none text-gray-700">
                  <p className="text-lg mb-4">
                    Groupe Fabre Environnement propose ses services de gestion, collecte et valorisation des déchets 
                    à {formattedCity} et dans toute l'agglomération. Notre proximité avec le territoire nous permet 
                    d'offrir des solutions rapides et adaptées aux besoins spécifiques de {formattedCity}.
                  </p>
                  <p className="mb-4">
                    Que vous soyez un particulier, une entreprise ou une collectivité de {formattedCity}, 
                    nous mettons à votre disposition notre expertise et notre flotte de véhicules pour 
                    tous vos besoins en matière de gestion des déchets.
                  </p>
                </div>
              </div>
            </section>

            <GeoLocalizedContent content={departmentData} showCTA={true} />
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default CityPage;
